import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { resolveAssetUrl } from "../../utils/api";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import MainCard from "components/MainCard";

import BusinessIcon   from "@mui/icons-material/Business";
import PhoneIcon      from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SettingsIcon   from "@mui/icons-material/Settings";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PaymentIcon    from "@mui/icons-material/Payment";
import ImageIcon      from "@mui/icons-material/Image";
import ArrowBackIcon  from "@mui/icons-material/ArrowBack";

import {
  Box, Grid, TextField, Button, Typography,
  MenuItem, Switch, FormControlLabel, CircularProgress, Alert
} from "@mui/material";

import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useToast } from "contexts/ToastContext";

dayjs.extend(customParseFormat);

const parseTime = (val) => {
  if (!val) return null;
  if (dayjs.isDayjs(val) && val.isValid()) return val;
  const parsed = dayjs(val, "HH:mm:ss", true);
  return parsed.isValid() ? parsed : null;
};

const formatTime = (val) => {
  if (!val) return "";
  if (dayjs.isDayjs(val) && val.isValid()) return val.format("HH:mm:ss");
  return "";
};

const toImgUrl = (path) => resolveAssetUrl(path);

export default function StoreEdit() {
  const navigate = useNavigate();
  const { id }   = useParams();
  const { showToast, showApiError } = useToast();

  const [pageLoading, setPageLoading] = useState(true);
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState("");

  const [branchGroups, setBranchGroups] = useState([]);
  const [storeTypes,   setStoreTypes]   = useState([]);
  const [logoPreview,  setLogoPreview]  = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);

  const [form, setForm] = useState({
    user_id:           "",
    branch_group_id:   "",
    store_type_id:     "",
    title_en:          "",
    title_ar:          "",
    address_en:        "",
    address_ar:        "",
    primary_contact:   "",
    secondary_contact: "",
    logo:              null,
    cover_image:       null,
    from_time:         null,
    to_time:           null,
    latitude:          "",
    longitude:         "",
    minimum_purchase:  "",
    commission_rate:   "",
    delivery_charge:   "",
    delivery_range:    "",
    cod_pay:    false,
    online_pay: false,
    wallet_pay: false,
    card_pay:   false,
    dining:     false,
    take_away:  false,
    delivery:   false,
    busy:       false,
    pure_veg:   false,
    status:     true,
  });

  useEffect(() => {
    Promise.all([loadDropdowns(), loadStore()])
      .finally(() => setPageLoading(false));
  }, [id]);

  const loadDropdowns = async () => {
    try {
      const [branchRes, storeTypeRes] = await Promise.all([
        api.get("/branch/group/list"),
        api.get("/store/type/list"),
      ]);
      setBranchGroups(Array.isArray(branchRes.data?.data)    ? branchRes.data.data    : []);
      setStoreTypes(  Array.isArray(storeTypeRes.data?.data) ? storeTypeRes.data.data : []);
    } catch {
      setBranchGroups([]);
      setStoreTypes([]);
    }
  };

  const loadStore = async () => {
    try {
      const res  = await api.get(`/store/${id}`);
      const data = res.data?.data || res.data;
      if (!data) throw new Error("No data returned");

      setForm({
        user_id:           data.user_id           || "",
        branch_group_id:   data.branch_group_id   || "",
        store_type_id:     data.store_type_id      || "",
        title_en:          data.title_en           || "",
        title_ar:          data.title_ar           || "",
        address_en:        data.address_en         || "",
        address_ar:        data.address_ar         || "",
        primary_contact:   data.primary_contact    || "",
        secondary_contact: data.secondary_contact  || "",
        logo:              null,
        cover_image:       null,
        from_time:         parseTime(data.from_time),
        to_time:           parseTime(data.to_time),
        latitude:          data.latitude          ?? "",
        longitude:         data.longitude         ?? "",
        minimum_purchase:  data.minimum_purchase  ?? "",
        commission_rate:   data.commission_rate   ?? "",
        delivery_charge:   data.delivery_charge   ?? "",
        delivery_range:    data.delivery_range    ?? "",
        cod_pay:    !!data.cod_pay,
        online_pay: !!data.online_pay,
        wallet_pay: !!data.wallet_pay,
        card_pay:   !!data.card_pay,
        dining:     !!data.dining,
        take_away:  !!data.take_away,
        delivery:   !!data.delivery,
        busy:       !!data.busy,
        pure_veg:   !!data.pure_veg,
        status:     data.status !== undefined ? !!data.status : true,
      });

      if (data.logo)        setLogoPreview( toImgUrl(data.logo));
      if (data.cover_image) setCoverPreview(toImgUrl(data.cover_image));

    } catch (err) {
      showToast("Failed to load store data");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (!files?.[0]) return;
    const file = files[0];
    setForm(prev => ({ ...prev, [name]: file }));
    if (name === "logo")        setLogoPreview( URL.createObjectURL(file));
    if (name === "cover_image") setCoverPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.store_type_id || !form.title_en.trim()) {
      setError("Store Type and Title (English) are required.");
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();

      Object.keys(form).forEach((key) => {
        const val = form[key];

        if (key === "from_time") {
          fd.append(key, formatTime(val));
        } else if (key === "to_time") {
          fd.append(key, formatTime(val));
        } else if (val instanceof File) {
          fd.append(key, val);
        } else if (key === "logo" || key === "cover_image") {
          // skip null — backend keeps existing image
        } else if (val === null || val === undefined) {
          fd.append(key, "");
        } else {
          fd.append(key, val);
        }
      });

      await api.put(`/store/edit/${id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      navigate("/store/list");

    } catch (err) {
      showApiError(err, "Update failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (pageLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const SectionTitle = ({ icon, title }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {icon}
      <Typography variant="h6">{title}</Typography>
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button 
            variant="outlined" 
            color="secondary" 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate(-1)}
            sx={{ borderRadius: 2 }}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" fontWeight={700}>Edit Store</Typography>
            <Typography variant="body2" color="text.secondary">
              Update the details for <strong>{form.title_en}</strong>
            </Typography>
          </Box>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>

            {error && (
              <Grid size={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}

            {/* ── BASIC INFO ─────────────────────────────────────────── */}
            <Grid size={12}>
              <MainCard
                contentSX={{ p: 3 }} sx={{ borderRadius: 3 }}
                title={<SectionTitle icon={<BusinessIcon color="primary" />} title="Basic Information" />}
              >
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField select fullWidth label="Branch Group"
                      name="branch_group_id" value={form.branch_group_id || ""} onChange={handleChange}>
                      <MenuItem value=""><em>Select Branch</em></MenuItem>
                      {branchGroups.map((bg) => (
                        <MenuItem key={bg.branch_group_id} value={bg.branch_group_id}>
                          {bg.franchise_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField select fullWidth required label="Store Type *"
                      name="store_type_id" value={form.store_type_id || ""} onChange={handleChange}>
                      <MenuItem value=""><em>Select Store Type</em></MenuItem>
                      {storeTypes.map((st) => (
                        <MenuItem key={st.store_type_id} value={st.store_type_id}>
                          {st.type_name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField fullWidth required label="Title English *"
                      name="title_en" value={form.title_en} onChange={handleChange} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField fullWidth label="Title Arabic"
                      name="title_ar" value={form.title_ar} onChange={handleChange} />
                  </Grid>
                </Grid>
              </MainCard>
            </Grid>

            {/* ── CONTACT ────────────────────────────────────────────── */}
            <Grid size={12}>
              <MainCard contentSX={{ p: 3 }} sx={{ borderRadius: 3 }}
                title={<SectionTitle icon={<PhoneIcon color="primary" />} title="Contact Information" />}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField fullWidth label="Primary Contact"
                      name="primary_contact" value={form.primary_contact} onChange={handleChange} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField fullWidth label="Secondary Contact"
                      name="secondary_contact" value={form.secondary_contact} onChange={handleChange} />
                  </Grid>
                </Grid>
              </MainCard>
            </Grid>

            {/* ── LOCATION ───────────────────────────────────────────── */}
            <Grid size={12}>
              <MainCard contentSX={{ p: 3 }} sx={{ borderRadius: 3 }}
                title={<SectionTitle icon={<LocationOnIcon color="primary" />} title="Location Information" />}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField type="number" fullWidth label="Latitude"
                      name="latitude" value={form.latitude} onChange={handleChange} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField type="number" fullWidth label="Longitude"
                      name="longitude" value={form.longitude} onChange={handleChange} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField fullWidth label="Address English"
                      name="address_en" value={form.address_en} onChange={handleChange} />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField fullWidth label="Address Arabic"
                      name="address_ar" value={form.address_ar} onChange={handleChange} />
                  </Grid>
                </Grid>
              </MainCard>
            </Grid>

            {/* ── TIMING ─────────────────────────────────────────────── */}
            <Grid size={12}>
              <MainCard contentSX={{ p: 3 }} sx={{ borderRadius: 3 }}
                title={<SectionTitle icon={<AccessTimeIcon color="primary" />} title="Store Timing" />}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TimePicker
                      label="Opening Time"
                      value={form.from_time}
                      onChange={(val) => setForm(prev => ({ ...prev, from_time: val }))}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TimePicker
                      label="Closing Time"
                      value={form.to_time}
                      onChange={(val) => setForm(prev => ({ ...prev, to_time: val }))}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  </Grid>
                </Grid>
              </MainCard>
            </Grid>

            {/* ── DELIVERY SETTINGS ──────────────────────────────────── */}
            <Grid size={12}>
              <MainCard contentSX={{ p: 3 }} sx={{ borderRadius: 3 }}
                title={<SectionTitle icon={<SettingsIcon color="primary" />} title="Delivery Settings" />}>
                <Grid container spacing={3}>
                  {[
                    { label: "Minimum Purchase",   name: "minimum_purchase" },
                    { label: "Commission Rate (%)", name: "commission_rate"  },
                    { label: "Delivery Charge",     name: "delivery_charge"  },
                    { label: "Delivery Range (KM)", name: "delivery_range"   },
                  ].map((f) => (
                    <Grid size={{ xs: 12, md: 3 }} key={f.name}>
                      <TextField type="number" fullWidth label={f.label}
                        name={f.name} value={form[f.name]} onChange={handleChange} />
                    </Grid>
                  ))}
                </Grid>
              </MainCard>
            </Grid>

            {/* ── PAYMENT METHODS ────────────────────────────────────── */}
            <Grid size={12}>
              <MainCard contentSX={{ p: 3 }} sx={{ borderRadius: 3 }}
                title={<SectionTitle icon={<PaymentIcon color="primary" />} title="Payment Methods" />}>
                <Grid container spacing={2}>
                  {[
                    { label: "COD",        name: "cod_pay"    },
                    { label: "Online Pay", name: "online_pay" },
                    { label: "Wallet Pay", name: "wallet_pay" },
                    { label: "Card Pay",   name: "card_pay"   },
                  ].map((item) => (
                    <Grid size={{ xs: 12, md: 3 }} key={item.name}>
                      <FormControlLabel
                        control={
                          <Switch checked={!!form[item.name]}
                            onChange={(e) => setForm(p => ({ ...p, [item.name]: e.target.checked }))} />
                        }
                        label={item.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </MainCard>
            </Grid>

            {/* ── STORE FEATURES ─────────────────────────────────────── */}
            <Grid size={12}>
              <MainCard contentSX={{ p: 3 }} sx={{ borderRadius: 3 }}
                title={<SectionTitle icon={<BusinessIcon color="primary" />} title="Store Features" />}>
                <Grid container spacing={2}>
                  {[
                    { label: "Dining",        name: "dining"    },
                    { label: "Take Away",     name: "take_away" },
                    { label: "Delivery",      name: "delivery"  },
                    { label: "Busy",          name: "busy"      },
                    { label: "Pure Veg",      name: "pure_veg"  },
                    { label: "Active Status", name: "status"    },
                  ].map((item) => (
                    <Grid size={{ xs: 12, md: 3 }} key={item.name}>
                      <FormControlLabel
                        control={
                          <Switch checked={!!form[item.name]}
                            onChange={(e) => setForm(p => ({ ...p, [item.name]: e.target.checked }))} />
                        }
                        label={item.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </MainCard>
            </Grid>

            {/* ── STORE IMAGES ───────────────────────────────────────── */}
            <Grid size={12}>
              <MainCard contentSX={{ p: 3 }} sx={{ borderRadius: 3 }}
                title={<SectionTitle icon={<ImageIcon color="primary" />} title="Store Images" />}>
                <Grid container spacing={3}>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Button variant="outlined" component="label" fullWidth>
                      {form.logo ? "Change Logo" : logoPreview ? "Replace Logo" : "Upload Logo"}
                      <input type="file" name="logo" hidden accept="image/*" onChange={handleFileChange} />
                    </Button>
                    {logoPreview && (
                      <Box mt={1}>
                        <img src={logoPreview} alt="Logo"
                          style={{ height: 80, borderRadius: 8, objectFit: "cover" }} />
                      </Box>
                    )}
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Button variant="outlined" component="label" fullWidth>
                      {form.cover_image ? "Change Cover" : coverPreview ? "Replace Cover" : "Upload Cover Image"}
                      <input type="file" name="cover_image" hidden accept="image/*" onChange={handleFileChange} />
                    </Button>
                    {coverPreview && (
                      <Box mt={1}>
                        <img src={coverPreview} alt="Cover"
                          style={{ height: 120, width: "100%", borderRadius: 8, objectFit: "cover" }} />
                      </Box>
                    )}
                  </Grid>

                </Grid>
              </MainCard>
            </Grid>

            {/* ── STICKY ACTION BAR ──────────────────────────────────── */}
            <Grid size={12}>
              <Box sx={{
                position: "sticky", bottom: 0, zIndex: 10,
                background: "#fff", p: 2,
                borderTop: "1px solid #eee",
                display: "flex", justifyContent: "flex-end", gap: 2,
              }}>
                <Button variant="outlined" onClick={() => navigate("/store/list")} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" size="large" disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={18} /> : null}>
                  {submitting ? "Updating…" : "Update Store"}
                </Button>
              </Box>
            </Grid>

          </Grid>
        </form>
      </Box>
    </LocalizationProvider>
  );
}